import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import * as uuid from 'uuid';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { formatInTimeZone } from 'date-fns-tz';
import { MdOutlineInfo } from 'react-icons/md';
import {
  CrossChannelMediaIds,
  IncrementalityTestingEntry,
  IncrementalityTestingTable,
  OfflineInputTabs
} from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { Placement } from '@Components/designConstants';
import * as UserRedux from '../../../redux/user';
import { SetError } from '../../../redux/modals';
import { dateToMddyy } from '../../../utils/date-utils';
import {
  prepDeleteTableReq,
  validateAndUpdateTables as validateAndUpdateTablesInternal,
  QueuedRequest,
  prepCreateTableReq,
  tabularOfflineInputsDownload,
  IncrementalityTestKpi,
  IncrementalityTestingTableEditsMap,
  IncrementalityTestingNewTableEntriesMap,
  IncrementalityTestingEntryEditsMap,
  fetchCrossChannelMediaIds
} from './OfflineInputsUtils';
import {
  CommitSuccessDialog,
  DiscardConfirmationDialog,
  IncrementalityTestingInstructionsDialog,
  IncrementalityTestingValidationErrorDialog
} from './OfflineInputsDialogs';
import { InputContainer } from './InputContainer';
import { InputTable, getTablesContainerInnerWidth } from './InputTable';
import { AddIncrementalityTest } from './AddIncrementalityTest';

interface IncrementalityTestingProps {
  inputType: keyof typeof OfflineInputTabs;
  originalTables: IncrementalityTestingTable[];
  updated: { by?: string; at?: string };
  fetching: boolean;
  fetchFailed: boolean;
  refetch: () => void;
  editingUnlocked: boolean;
  setEditingUnlocked: Dispatch<SetStateAction<boolean>>;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  dispatchStateReset: boolean;
  setDispatchStateReset: Dispatch<SetStateAction<boolean>>;
  company: string;
  setError: SetError;
}

interface IncrementalityTableHeaderProps {
  table: IncrementalityTestingTable;
}

// Renders the per-table header (name, tooltip, test metadata) so the main
// component's table map stays shallow.
const IncrementalityTableHeader = ({ table }: IncrementalityTableHeaderProps) => {
  const {
    name,
    testType,
    hypothesis,
    location,
    outcomeSource,
    outcomeSourceSubset,
    lagWindow,
    startDate,
    endDate
  } = table;

  return (
    <div className='header-labels'>
      <div className='top-row'>
        <strong>{name}</strong>
        <OverlayTrigger
          placement={Placement.TOP}
          trigger={['hover', 'focus']}
          overlay={
            <Popover className='incrementality-testing-table-tooltip'>
              <Popover.Body>
                <div className='tooltip-text'>
                  <ul>
                    <li>
                      <strong>Test Location</strong>: {location}
                    </li>
                    <li>
                      <strong>Test Hypothesis</strong>: {hypothesis}
                    </li>
                  </ul>
                </div>
              </Popover.Body>
            </Popover>
          }
        >
          <span className='tooltip-container'>
            <MdOutlineInfo size={24} />
          </span>
        </OverlayTrigger>
      </div>
      <div>{`${testType} | Outcome Source: ${outcomeSource}, ${outcomeSourceSubset}`}</div>
      <div>{`${dateToMddyy(startDate)} - ${dateToMddyy(endDate)} | ${lagWindow} Day Lag`}</div>
    </div>
  );
};

export const IncrementalityTesting = ({
  inputType,
  originalTables,
  updated,
  fetching,
  fetchFailed,
  refetch,
  editingUnlocked,
  setEditingUnlocked,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  dispatchStateReset,
  setDispatchStateReset,
  company,
  setError
}: IncrementalityTestingProps) => {
  const [tables, setTables] = useState<IncrementalityTestingTable[]>([...originalTables]);
  const [crossChannelMediaIds, setCrossChannelMediaIds] = useState<CrossChannelMediaIds>({
    accountIds: [],
    campaignIds: []
  });
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [tablesInEditMode, setTablesInEditMode] = useState<string[]>([]);
  const [newTableEntries, setNewTableEntries] = useState<IncrementalityTestingNewTableEntriesMap>(
    {}
  );
  const [validationErrors, setValidationErrors] = useState<{ tableId: string; errors: string[] }[]>(
    []
  );
  const [queuedRequests, setQueuedRequests] = useState<QueuedRequest[]>([]);
  const [deletedTables, setDeletedTables] = useState<IncrementalityTestingTable[]>([]);
  const [showAddIncrementalityTestModal, setShowAddIncrementalityTestModal] = useState(false);
  const [showCommitSuccessDialog, setShowCommitSuccessDialog] = useState(false);
  const [showInstructionsDialog, setShowInstructionsDialog] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [tableEdits, setTableEdits] = useState<IncrementalityTestingTableEditsMap>({});
  const [filter, setFilter] = useState<{ filter: (line: Record<string, unknown>) => boolean }>();
  const userFullName: string = useSelector(UserRedux.fullNameSelector) ?? '';

  const scrollToRow = (entryId: string) => {
    const foundEl = document.getElementById(entryId);
    if (foundEl) {
      foundEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setTimeout(scrollToRow, 200, entryId);
    }
  };

  const startEditingTable = (tableId: string, _blanks: IncrementalityTestingEntry[]) => {
    setTablesInEditMode([...tablesInEditMode, tableId]);
    setTableEdits({ ...tableEdits, [tableId]: {} });
  };

  // Note: KPIs can only be added after edit button is clicked
  // so no need to add table to tablesInEditMode or setTableEdits
  const addIncrementalityTestingKpi = (tableId: string, blanks: IncrementalityTestingEntry[]) => {
    setNewTableEntries(prev => ({
      ...prev,
      [tableId]: prev[tableId] ? [...prev[tableId], ...blanks] : blanks
    }));
    if (blanks.length > 0) {
      scrollToRow(blanks[blanks.length - 1].entryId);
    }
  };

  const resetState = useCallback(() => {
    setTables([...originalTables]);
    setSubmitting(false);
    setEditingUnlocked(false);
    setTablesInEditMode([]);
    setHasUnsavedChanges(false);
    setNewTableEntries({});
    setValidationErrors([]);
    setQueuedRequests([]);
    setDeletedTables([]);
    setShowAddIncrementalityTestModal(false);
    setShowCommitSuccessDialog(false);
    setShowInstructionsDialog(false);
    setShowDiscardDialog(false);
    setTableEdits({});
    setFilter(undefined);
  }, [originalTables, setEditingUnlocked, setHasUnsavedChanges]);

  useEffect(() => {
    if (dispatchStateReset) {
      resetState();
      setDispatchStateReset(false);
    }
  }, [dispatchStateReset, resetState, setDispatchStateReset]);

  useEffect(() => {
    setTables([...originalTables]);
  }, [originalTables]);

  useEffect(() => {
    fetchCrossChannelMediaIds(company, setCrossChannelMediaIds, setError);
  }, [company, setCrossChannelMediaIds, setError]);

  useEffect(() => {
    setHasUnsavedChanges(Boolean(tablesInEditMode.length) || Boolean(deletedTables.length));
  }, [tablesInEditMode, deletedTables, setHasUnsavedChanges]);

  const onCommitSuccess = () => {
    refetch();
    resetState();
    setShowCommitSuccessDialog(true);
  };

  const validateAndUpdateTables = async () => {
    setSubmitting(true);
    await validateAndUpdateTablesInternal(
      company,
      userFullName,
      inputType,
      tableEdits,
      newTableEntries,
      setValidationErrors,
      setError,
      onCommitSuccess,
      queuedRequests,
      setQueuedRequests
    );
    setSubmitting(false);
  };

  const deleteTable = (tableId: string) => {
    prepDeleteTableReq<
      IncrementalityTestingTable,
      IncrementalityTestingTableEditsMap,
      IncrementalityTestingNewTableEntriesMap
    >(
      tableId,
      company,
      userFullName,
      inputType,
      tables,
      setTables,
      deletedTables,
      setDeletedTables,
      queuedRequests,
      setQueuedRequests,
      newTableEntries,
      setNewTableEntries,
      tableEdits,
      setTableEdits
    );
  };

  const handleCreateTable = (
    name: string,
    hypothesis: string,
    testType: string,
    location: string,
    outcomeSource: string,
    outcomeSourceSubset: string,
    lagWindow: number,
    mediaIdType: string,
    mediaIds: string[],
    kpis: IncrementalityTestKpi[],
    startDate: Date,
    endDate: Date
  ) => {
    const newTable: IncrementalityTestingTable = {
      tableId: uuid.v4(),
      name,
      hypothesis,
      testType,
      location,
      outcomeSource,
      outcomeSourceSubset,
      lagWindow,
      mediaIdType,
      mediaIds,
      startDate: formatInTimeZone(startDate, 'UTC', 'yyyy-MM-dd'),
      endDate: formatInTimeZone(endDate, 'UTC', 'yyyy-MM-dd'),
      entries: []
    };

    prepCreateTableReq<
      IncrementalityTestingTable,
      IncrementalityTestingTableEditsMap,
      IncrementalityTestingNewTableEntriesMap
    >({
      company,
      updatedBy: userFullName,
      inputType: OfflineInputTabs.INCREMENTALITY_TESTING,
      startDate,
      endDate,
      kpis,
      newTable,
      queuedRequests,
      setQueuedRequests,
      tables,
      setTables,
      tablesInEditMode,
      setTablesInEditMode,
      newTableEntries,
      setNewTableEntries,
      tableEdits,
      setTableEdits
    });
    setEditingUnlocked(true);
  };

  const download = useCallback(
    () => tabularOfflineInputsDownload(inputType, tables),
    [inputType, tables]
  );

  return (
    <InputContainer
      dialogs={
        <>
          <AddIncrementalityTest
            show={showAddIncrementalityTestModal}
            crossChannelMediaIds={crossChannelMediaIds}
            handleClose={() => setShowAddIncrementalityTestModal(false)}
            createTable={handleCreateTable}
          />
          <CommitSuccessDialog
            show={showCommitSuccessDialog}
            onHide={() => setShowCommitSuccessDialog(false)}
          />
          <IncrementalityTestingInstructionsDialog
            show={showInstructionsDialog}
            onHide={() => setShowInstructionsDialog(false)}
          />
          <DiscardConfirmationDialog
            show={showDiscardDialog}
            onDiscard={() => resetState()}
            onHide={() => setShowDiscardDialog(false)}
          />
          {validationErrors.length > 0 && (
            <IncrementalityTestingValidationErrorDialog
              errors={validationErrors[0].errors}
              onResolve={() => {
                const remaining = validationErrors.slice(1);
                setValidationErrors(remaining);

                if (remaining.length === 0) {
                  validateAndUpdateTables();
                }
              }}
              onReject={() => {
                scrollToRow(validationErrors[0].errors[0]);
                setValidationErrors([]);
                setShowInstructionsDialog(true);
              }}
              table={tables.find(table => table.tableId === validationErrors[0].tableId)}
              tableEdits={tableEdits[validationErrors[0].tableId]}
              getInputDescription={(table: IncrementalityTestingTable): string => table.name}
            />
          )}
        </>
      }
      tables={
        <div
          className='tables-container-inner'
          style={getTablesContainerInnerWidth(OfflineInputTabs.INCREMENTALITY_TESTING, tables)}
        >
          {tables?.map(table => (
            <InputTable
              inputType={OfflineInputTabs.INCREMENTALITY_TESTING}
              table={table}
              key={table.tableId}
              deleteTable={deleteTable}
              tablesInEditMode={tablesInEditMode}
              startEditingTable={startEditingTable}
              editingUnlocked={editingUnlocked}
              edits={tableEdits[table.tableId] ?? {}}
              setEdits={(edits: IncrementalityTestingEntryEditsMap) =>
                setTableEdits({ ...tableEdits, [table.tableId]: edits })
              }
              newEntries={newTableEntries[table.tableId] ?? []}
              submitting={submitting}
              setError={setError}
              filter={filter ? filter.filter : undefined}
              headerLabels={<IncrementalityTableHeader table={table} />}
              addIncrementalityTestingKpi={addIncrementalityTestingKpi}
            />
          ))}
        </div>
      }
      inputType={inputType}
      updated={updated}
      isEmpty={tables.length + deletedTables.length === 0}
      fetching={fetching}
      fetchFailed={fetchFailed}
      refetch={refetch}
      submitting={submitting}
      editingUnlocked={editingUnlocked}
      setEditingUnlocked={setEditingUnlocked}
      hasUnsavedChanges={hasUnsavedChanges}
      onFilter={newFilter => {
        setFilter({ filter: newFilter });
      }}
      validateAndUpdateTables={validateAndUpdateTables}
      setShowAddInputModal={setShowAddIncrementalityTestModal}
      setShowDiscardDialog={setShowDiscardDialog}
      setError={setError}
      download={download}
    />
  );
};
